import { isContentClean, getFilterMessage } from '../utils/contentFilter';
import { isStoreSafe } from '../utils/storeSafe';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, useToast, Checkbox, Text } from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { geoService } from '../services/geoService';
import userService from '../services/userService';
import { ProfileAvatar } from '../components/profile';
import AvatarSelector from '../components/avatar/AvatarSelector';
import './OnboardingPage.css';

// ============================================================================
//  INVITE SERVICE - Handle post-onboarding redirect to game

// ============================================================================
import { getAndClearPostOnboardingRedirect } from '../services/inviteService';
import kycService from '../services/kycService';

// ============================================================================
// ONBOARDING PAGE - Multi-step new user setup
// ============================================================================
// Steps:
// 1. Location Check - Verify geo compliance (blocks WA, ID for real money)
// 2. Age Verification - Confirm 18+ with date of birth
// 3. Terms & Rules - Accept Terms, Privacy, Rules, Responsible Gaming
// 4. Avatar Selection - Choose DiceBear avatar or upload custom
// 5. Profile Setup - Username customization
// 6. Complete - Welcome and redirect to home
// ============================================================================
//  FIX: External links now open in system browser on mobile (Capacitor)

//  FIX: This prevents users from bypassing onboarding via compliance links

//  FIX: Use AuthContext loading state to prevent race conditions

// ============================================================================

type OnboardingStep = 'location' | 'age' | 'terms' | 'avatar' | 'profile' | 'verify' | 'complete';

const MINIMUM_AGE = 18;

const OnboardingPage: React.FC = () => {
  const { currentUser, userProfile, loading: authLoading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  //  FIX: Guard to prevent double completion

  const onboardingCompletedRef = useRef(false);

  const [step, setStep] = useState<OnboardingStep>('location');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  // Location step
  const [geoAllowed, setGeoAllowed] = useState<boolean | null>(null);
  const [geoLocation, setGeoLocation] = useState<string>('');
  const [geoError, setGeoError] = useState<string>('');

  // Avatar step
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');

  // Age verification step
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [ageError, setAgeError] = useState('');

  // Terms acceptance step
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [responsibleGamingAccepted, setResponsibleGamingAccepted] = useState(false);

  // Profile step
  const [username, setUsername] = useState('');
  const [photoURL, setPhotoURL] = useState<string | undefined>();

  // ============================================================================
  //  EXTERNAL LINK HANDLER - Opens in new tab, user stays on onboarding

  // ============================================================================
  const openExternalLink = useCallback((tab: string) => {
    const url = `${window.location.origin}/legal?tab=${tab}`;
    // Open in new tab - works on both web and mobile webview
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // ============================================================================
  // CHECK IF ALREADY COMPLETED ONBOARDING
  //  FIX: Use userProfile from AuthContext instead of fetching again

  //  BULLETPROOF: Use ref to completely block this effect during completion

  // ============================================================================
  const isCompletingRef = useRef(false);
  const navigationCompletedRef = useRef(false); //  FIX: Prevent multiple navigations

  
  useEffect(() => {
    const checkOnboarding = async () => {
      //  BULLETPROOF: If we're in the middle of completing, do nothing

      if (isCompletingRef.current || onboardingCompletedRef.current) {
        setLoading(false);
        return;
      }
      
      //  FIX: Wait for auth to finish loading

      if (authLoading) {
        return; // Keep loading true while auth is loading
      }

      if (!currentUser) {
        if (!navigationCompletedRef.current) {
          navigationCompletedRef.current = true;
          navigate('/login');
        }
        return;
      }

      //  FIX: Don't navigate if we're already on complete step

      if (step === 'complete' || step === 'verify') {
        setLoading(false);
        return;
      }

      //  FIX: Use userProfile from AuthContext (single source of truth)

      //  FIX: Only navigate for returning users, NOT during completion

      // If we're completing onboarding, don't redirect - let the completion flow handle it
      if (userProfile?.onboardingCompleted && step !== 'complete' && step !== 'verify' && !isCompletingRef.current) {
        if (!navigationCompletedRef.current) {
          navigationCompletedRef.current = true;
          navigate('/', { replace: true });
        }
        return;
      }

      // Initialize with current profile data from AuthContext. Prefill
      // ONCE and never stomp what the user has typed (this effect re-runs
      // whenever the profile refreshes). Falls back to the username chosen
      // at signup (sessionStorage) when the profile write is still in
      // flight on a slow backend - the completion write persists it.
      const signupStash = (() => {
        try { return sessionStorage.getItem('pss:signupUsername') || ''; } catch { return ''; }
      })();
      setUsername(prev => prev || userProfile?.username || signupStash);
      if (userProfile) {
        setPhotoURL(userProfile.photoURL);
        if (userProfile.photoURL) {
          setSelectedAvatar(userProfile.photoURL);
        }
      }

      //  FIX: Always set loading false once auth is done

      setLoading(false);
    };

    checkOnboarding();
  }, [currentUser, userProfile, authLoading, navigate, step]);

  // ============================================================================
  // STEP 1: LOCATION CHECK
  // ============================================================================
  const handleLocationCheck = async () => {
    setChecking(true);
    setGeoError('');

    try {
      // Hard ceiling on the whole check: whatever hangs underneath
      // (backend read, geo API), the spinner must never be permanent.
      const result = await Promise.race([
        geoService.checkLocation(),
        new Promise<any>((_, reject) =>
          setTimeout(() => reject(new Error('Location check timed out - please try again')), 15000)),
      ]);

      if (result.allowed) {
        setGeoAllowed(true);
        setGeoLocation(`${result.city || ''}, ${result.regionName || result.region || ''}`);
        
        // Small delay for UX then proceed to age verification
        setTimeout(() => setStep('age'), 1000);
      } else {
        setGeoAllowed(false);
        setGeoLocation(result.regionName || result.region || 'your location');
        setGeoError(result.reason || 'Location not allowed');
      }
    } catch (error) {
      console.error('Geo check error:', error);
      setGeoAllowed(false);
      setGeoError('Unable to verify location. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  // Auto-check location on mount
  useEffect(() => {
    if (!loading && step === 'location') {
      handleLocationCheck();
    }
  }, [loading, step]);

  // ============================================================================
  // STEP 2: AGE VERIFICATION
  // ============================================================================
  const calculateAge = (month: number, day: number, year: number): number => {
    const today = new Date();
    const birthDate = new Date(year, month - 1, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleAgeVerification = async () => {
    setAgeError('');
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);
    const year = parseInt(birthYear);
    
    if (!month || !day || !year) {
      setAgeError('Please enter your complete date of birth');
      return;
    }
    if (month < 1 || month > 12) {
      setAgeError('Please enter a valid month (1-12)');
      return;
    }
    if (day < 1 || day > 31) {
      setAgeError('Please enter a valid day (1-31)');
      return;
    }
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      setAgeError('Please enter a valid year');
      return;
    }
    
    const age = calculateAge(month, day, year);
    if (age < MINIMUM_AGE) {
      setAgeError(`You must be at least ${MINIMUM_AGE} years old to use this platform.`);
      return;
    }
    
    setChecking(true);
    try {
      if (currentUser) {
        const dateOfBirth = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        await userService.updateUserProfile(currentUser.uid, {
          dateOfBirth,
          ageVerifiedAt: new Date(),
        });
      }
      //  FIX: Add small delay before step change to prevent navigation throttling

      await new Promise(resolve => setTimeout(resolve, 50));
      setStep('terms');
    } catch (error) {
      console.error('Age verification error:', error);
      setAgeError('Failed to save. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  // ============================================================================
  // STEP 3: TERMS & RULES ACCEPTANCE
  // ============================================================================
  const allTermsAccepted = termsAccepted && privacyAccepted && rulesAccepted && responsibleGamingAccepted;

  const handleTermsAcceptance = async () => {
    if (!allTermsAccepted) {
      toast({
        title: 'Please accept all agreements',
        description: 'You must accept all terms to continue.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    
    setChecking(true);
    try {
      if (currentUser) {
        await userService.updateUserProfile(currentUser.uid, {
          termsAcceptedAt: new Date(),
          privacyAcceptedAt: new Date(),
          rulesAcceptedAt: new Date(),
          responsibleGamingAcceptedAt: new Date(),
        });
      }
      //  FIX: Add small delay before step change to prevent navigation throttling

      await new Promise(resolve => setTimeout(resolve, 50));
      setStep('avatar');
    } catch (error) {
      console.error('Terms acceptance error:', error);
      toast({
        title: 'Error saving',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setChecking(false);
    }
  };

  // ============================================================================
  // STEP 4: AVATAR SELECTION
  // ============================================================================
  const handleAvatarSelect = (avatarUrl: string) => {
    setSelectedAvatar(avatarUrl);
    setPhotoURL(avatarUrl);
  };

  const handleAvatarContinue = async () => {
    if (!currentUser) return;

    setChecking(true);
    try {
      // Save avatar selection
      if (selectedAvatar) {
        await userService.updateProfilePhoto(currentUser.uid, selectedAvatar);
      }
      //  FIX: Add small delay before step change to prevent navigation throttling

      await new Promise(resolve => setTimeout(resolve, 50));
      setStep('profile');
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message || 'Could not save avatar',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setChecking(false);
    }
  };

  // ============================================================================
  // STEP 5: PROFILE SETUP (goes directly to complete - no payment step)
  // ============================================================================
  const handleProfileUpdate = async () => {
    if (!currentUser) return;

    setChecking(true);
    try {
      //  FIX: Batch username update WITH completion to reduce Firestore writes

      // This prevents rapid sequential writes that cause navigation throttling
      const updates: Record<string, any> = {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date(),
      };
      
      if (username && !isContentClean(username)) {
        toast({ title: 'Invalid username', description: getFilterMessage('username'), status: 'error', duration: 4000 });
        return;
      }
      if (username && username !== userProfile?.username) {
        updates.username = username.toLowerCase();
      }
      
      // Single batched update instead of two separate calls
      await userService.updateUserProfile(currentUser.uid, updates);

      // Account is complete. One optional step left: identity verification,
      // which can also be done later from the Wallet.
      onboardingCompletedRef.current = true;
      isCompletingRef.current = true;
      await refreshProfile();
      setChecking(false);
      setStep(isStoreSafe() ? 'complete' : 'verify');
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message || 'Could not update profile',
        status: 'error',
        duration: 3000,
      });
      setChecking(false);
    }
  };

  const handlePhotoUpdate = (newPhotoURL: string) => {
    setPhotoURL(newPhotoURL);
    setSelectedAvatar(newPhotoURL);
  };

  // ============================================================================
  // FINISH ONBOARDING - Separated from profile update to prevent throttling
  // ============================================================================
  // From the optional identity step: either leave for Didit's hosted flow
  // (the account is already complete, so they come back to the Wallet), or
  // go straight to the home screen.
  const [startingKyc, setStartingKyc] = useState(false);
  const goHome = () => {
    setStep('complete');
    const postOnboardingRedirect = getAndClearPostOnboardingRedirect();
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (!navigationCompletedRef.current) {
          navigationCompletedRef.current = true;
          navigate(postOnboardingRedirect || '/', { replace: true });
        }
      });
    }, 1200);
  };
  const startIdentityVerification = async () => {
    setStartingKyc(true);
    const r = await kycService.createVerificationSession(`${window.location.origin}/wallet?verification=complete`);
    if ('error' in r) {
      setStartingKyc(false);
      toast({ title: 'Could not start verification', description: `${r.error}. You can do this any time from your Wallet.`, status: 'warning', duration: 5000 });
      return;
    }
    kycService.redirectToVerification(r.url);
  };

  const finishOnboarding = async () => {
    if (!currentUser) return;
    
    // Prevent double execution
    if (onboardingCompletedRef.current) {
      return;
    }
    onboardingCompletedRef.current = true;
    isCompletingRef.current = true;

    // Refresh profile with delay
    await refreshProfile();

    // Set complete step with delay to prevent throttling
    await new Promise(resolve => setTimeout(resolve, 100));
    setStep('complete');

    // Check for post-onboarding redirect (e.g., from invite link)
    const postOnboardingRedirect = getAndClearPostOnboardingRedirect();
    
    //  FIX: Navigate directly to home page, not /welcome

    // The tutorial will be triggered from HomePage/App.tsx for new users
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (!navigationCompletedRef.current) {
          navigationCompletedRef.current = true;
          // Use postOnboardingRedirect if set, otherwise go to home
          navigate(postOnboardingRedirect || '/', { replace: true });
        }
      });
    }, 1500);
    
    setChecking(false);
  };

  // ============================================================================
  // COMPLETE ONBOARDING (called from skip buttons)
  //  FIX: Added guard to prevent double completion

  // ============================================================================
  const completeOnboarding = async () => {
    if (!currentUser) return;

    //  FIX: Prevent double execution

    if (onboardingCompletedRef.current) {
      console.log('⚠️ [Onboarding] Already completing, skipping duplicate call');
      return;
    }
    onboardingCompletedRef.current = true;
    isCompletingRef.current = true; //  BULLETPROOF: Block useEffect


    setChecking(true);
    try {
      // Mark onboarding as complete
      await userService.updateUserProfile(currentUser.uid, {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date(),
      });

      // Refresh profile
      await refreshProfile();

      // Optional identity step before the home screen
      await new Promise(resolve => setTimeout(resolve, 100));
      setStep(isStoreSafe() ? 'complete' : 'verify');

    } catch (error) {
      console.error('Complete onboarding error:', error);
      // Reset guard on error so user can retry
      onboardingCompletedRef.current = false;
    } finally {
      setChecking(false);
    }
  };

  // ============================================================================
  // RENDER LOADING
  // ============================================================================
  if (loading) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-loading">
          <div className="onboarding-spinner" />
          <span style={{ color: '#888' }}>Loading...</span>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER PROGRESS
  // ============================================================================
  const renderProgress = () => {
    const steps: OnboardingStep[] = ['location', 'age', 'terms', 'avatar', 'profile', 'verify', 'complete'];
    const currentIndex = steps.indexOf(step);

    return (
      <div className="onboarding-progress">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`progress-dot ${
                i < currentIndex ? 'completed' : i === currentIndex ? 'active' : ''
              }`}
            />
            {i < steps.length - 1 && (
              <div className={`progress-line ${i < currentIndex ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ============================================================================
  // STEP RENDERS
  // ============================================================================

  // Step 1: Location
  const renderLocationStep = () => (
    <div className="onboarding-card">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">Verifying Your Location</h2>
      <p className="onboarding-subtitle">
        We need to verify your location for sweepstakes compliance.
        This only takes a moment.
      </p>

      {checking && (
        <div className="onboarding-loading">
          <div className="onboarding-spinner" />
          <span style={{ color: '#888' }}>Checking location...</span>
        </div>
      )}

      {geoAllowed === true && (
        <div className="onboarding-success">
          <span className="onboarding-success-text">
             Location verified: {geoLocation}

          </span>
        </div>
      )}

      {geoAllowed === false && (
        <>
          <div className="onboarding-error">
            <div className="onboarding-error-icon"></div>

            <h3 className="onboarding-error-title">PSS Coins Not Available</h3>
            <p className="onboarding-error-text">
              PSS Coins prizes are not available in {geoLocation} due to state regulations.
              You can still play with PlayCoins for free!
            </p>
          </div>
          <button
            className="onboarding-btn primary"
            onClick={() => setStep('age')}
          >
            Continue with PlayCoins
          </button>
        </>
      )}

      {geoError && !geoAllowed && geoAllowed !== false && (
        <>
          <div className="onboarding-error">
            <p className="onboarding-error-text">{geoError}</p>
          </div>
          <button
            className="onboarding-btn primary"
            onClick={handleLocationCheck}
            disabled={checking}
          >
            Try Again
          </button>
        </>
      )}
    </div>
  );

  // Step 2: Age Verification
  const renderAgeStep = () => (
    <div className="onboarding-card">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">Verify Your Age</h2>
      <p className="onboarding-subtitle">
        You must be at least {MINIMUM_AGE} years old to use PokerSweepStars.
        Please enter your date of birth.
      </p>

      <div className="age-input-section">
        <div className="age-inputs">
          <div className="age-input-group">
            <label className="age-input-label">Month</label>
            <input
              type="number"
              className="age-input"
              placeholder="MM"
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value.slice(0, 2))}
              min="1"
              max="12"
            />
          </div>
          <div className="age-input-group">
            <label className="age-input-label">Day</label>
            <input
              type="number"
              className="age-input"
              placeholder="DD"
              value={birthDay}
              onChange={(e) => setBirthDay(e.target.value.slice(0, 2))}
              min="1"
              max="31"
            />
          </div>
          <div className="age-input-group">
            <label className="age-input-label">Year</label>
            <input
              type="number"
              className="age-input year-input"
              placeholder="YYYY"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value.slice(0, 4))}
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>
        </div>

        {ageError && (
          <div className="onboarding-error" style={{ marginTop: '16px' }}>
            <p className="onboarding-error-text">{ageError}</p>
          </div>
        )}
      </div>

      <button
        className="onboarding-btn primary"
        onClick={handleAgeVerification}
        disabled={checking || !birthMonth || !birthDay || !birthYear}
      >
        {checking ? 'Verifying...' : 'Verify Age'}
      </button>
      <button
        className="onboarding-btn back"
        onClick={() => setStep('location')}
        disabled={checking}
      >
        ← Back
      </button>
    </div>
  );

  // ============================================================================
  //  FIX: Step 3 - Terms with EXTERNAL LINK HANDLER (prevents bypass!)

  // ============================================================================
  const renderTermsStep = () => (
    <div className="onboarding-card terms-card">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">Accept Our Terms</h2>
      <p className="onboarding-subtitle">
        Please review and accept our terms to continue.
      </p>

      <div className="terms-checkboxes">
        <label className="terms-checkbox-item">
          <Checkbox
            isChecked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            colorScheme="cyan"
            size="lg"
          />
          <span className="terms-checkbox-text">
            I agree to the{' '}
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openExternalLink('terms');
              }}
              style={{ 
                color: '#00D9D9', 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
            >
              Terms of Service
            </span>
          </span>
        </label>

        <label className="terms-checkbox-item">
          <Checkbox
            isChecked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            colorScheme="cyan"
            size="lg"
          />
          <span className="terms-checkbox-text">
            I agree to the{' '}
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openExternalLink('privacy');
              }}
              style={{ 
                color: '#00D9D9', 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
            >
              Privacy Policy
            </span>
          </span>
        </label>

        <label className="terms-checkbox-item">
          <Checkbox
            isChecked={rulesAccepted}
            onChange={(e) => setRulesAccepted(e.target.checked)}
            colorScheme="cyan"
            size="lg"
          />
          <span className="terms-checkbox-text">
            I agree to the{' '}
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openExternalLink('rules');
              }}
              style={{ 
                color: '#00D9D9', 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
            >
              Official Sweepstakes Rules
            </span>
          </span>
        </label>

        <label className="terms-checkbox-item">
          <Checkbox
            isChecked={responsibleGamingAccepted}
            onChange={(e) => setResponsibleGamingAccepted(e.target.checked)}
            colorScheme="cyan"
            size="lg"
          />
          <span className="terms-checkbox-text">
            I acknowledge the{' '}
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openExternalLink('responsible');
              }}
              style={{ 
                color: '#00D9D9', 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
            >
              Responsible Gaming Policy
            </span>
          </span>
        </label>

        <div className="terms-summary">
          <Text fontSize="xs" color="gray.500" textAlign="center">
            By continuing, I confirm I am at least {MINIMUM_AGE} years old and understand that PSS Coins prizes are subject to applicable laws.
          </Text>
        </div>
      </div>

      <button
        className="onboarding-btn primary"
        onClick={handleTermsAcceptance}
        disabled={checking || !allTermsAccepted}
      >
        {checking ? 'Saving...' : 'I Accept'}
      </button>
      <button
        className="onboarding-btn back"
        onClick={() => setStep('age')}
        disabled={checking}
      >
        ← Back
      </button>
    </div>
  );

  // Step 4: Avatar Selection
  const renderAvatarStep = () => (
    <div className="onboarding-card avatar-step">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">Choose Your Avatar</h2>
      <p className="onboarding-subtitle">
        Select a style that represents you at the poker table.
        You can always change this later in your profile.
      </p>

      <div className="avatar-selector-wrapper">
        {currentUser && (
          <AvatarSelector
            currentAvatar={selectedAvatar}
            onAvatarSelect={handleAvatarSelect}
            userId={currentUser.uid}
          />
        )}
      </div>

      <button
        className="onboarding-btn primary"
        onClick={handleAvatarContinue}
        disabled={checking || !selectedAvatar}
      >
        {checking ? 'Saving...' : 'Continue'}
      </button>
      <button
        className="onboarding-btn secondary"
        onClick={() => setStep('profile')}
        disabled={checking}
      >
        Skip for Now
      </button>
      <button
        className="onboarding-btn back"
        onClick={() => setStep('terms')}
        disabled={checking}
      >
        ← Back
      </button>
    </div>
  );

  // Step 5: Profile
  const renderProfileStep = () => (
    <div className="onboarding-card">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">Customize Your Profile</h2>
      <p className="onboarding-subtitle">
        Add a photo and customize your username. You can always change these later.
      </p>

      <div className="profile-edit-section">
        <div className="profile-avatar-wrapper">
          {currentUser && (
            <ProfileAvatar
              userId={currentUser.uid}
              photoURL={photoURL || selectedAvatar}
              username={username}
              size="2xl"
              editable={true}
              onPhotoUpdate={handlePhotoUpdate}
            />
          )}
        </div>

        <div className="profile-input-group">
          <label className="profile-input-label">Username</label>
          <input
            type="text"
            className="profile-input"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="Enter username"
            maxLength={20}
          />
        </div>
      </div>

      <button
        className="onboarding-btn primary"
        onClick={handleProfileUpdate}
        disabled={checking || !username}
      >
        {checking ? 'Saving...' : 'Continue'}
      </button>
      <button
        className="onboarding-btn secondary"
        onClick={completeOnboarding}
        disabled={checking}
      >
        Skip for Now
      </button>
      <button
        className="onboarding-btn back"
        onClick={() => setStep('avatar')}
        disabled={checking}
      >
        ← Back
      </button>
    </div>
  );

  // Step 6: Complete
  const renderVerifyStep = () => (
    <div className="onboarding-card">
      <h2 className="onboarding-title">Confirm you're eligible for prizes</h2>
      <p className="onboarding-subtitle">
        Optional right now. It takes about two minutes with a photo ID, and you only need it
        before you redeem PSS Coins for the first time. Do it now, or later from your Wallet.
      </p>
      <Box bg="gray.800" p={4} borderRadius="lg" mt={2} mb={4} border="1px solid" borderColor="cyan.700">
        <Text color="cyan.400" fontWeight="semibold" mb={1}>What happens</Text>
        <Text color="gray.300" fontSize="sm">
          You'll be taken to our verification partner, snap your ID and a quick selfie, and land
          back in your Wallet. Must be 18 or older. Your documents are used only to confirm who you are.
        </Text>
      </Box>
      <button className="onboarding-btn primary" onClick={startIdentityVerification} disabled={startingKyc}>
        {startingKyc ? 'Opening verification...' : 'Verify now'}
      </button>
      <button className="onboarding-btn secondary" onClick={goHome} disabled={startingKyc} style={{ marginTop: 10 }}>
        Skip for now, I'll do it from my Profile
      </button>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="onboarding-card">
      <div className="onboarding-icon"></div>

      <h2 className="onboarding-title">You're All Set!</h2>
      <p className="onboarding-subtitle">
        Welcome to PokerSweepStars! You're ready to play.
      </p>

      <div className="onboarding-success">
        <span className="onboarding-success-text">
           Account setup complete

        </span>
      </div>

      <Box 
        bg="gray.800" 
        p={4} 
        borderRadius="lg" 
        mt={4}
        border="1px solid"
        borderColor="cyan.700"
      >
        <Text color="cyan.400" fontWeight="semibold" mb={2}>
           New to PokerSweepStars?

        </Text>
        <Text color="gray.300" fontSize="sm">
          Check out our interactive tutorial anytime by going to{' '}
          <Text as="span" color="cyan.400" fontWeight="bold">Profile → App Tutorials</Text>
          {' '}to learn how to play, earn PSS Coins, and get the most out of your experience.
        </Text>
      </Box>

      <div className="onboarding-loading">
        <div className="onboarding-spinner" />
        <span style={{ color: '#888' }}>Redirecting to home...</span>
      </div>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        {renderProgress()}

        {step === 'location' && renderLocationStep()}
        {step === 'age' && renderAgeStep()}
        {step === 'terms' && renderTermsStep()}
        {step === 'avatar' && renderAvatarStep()}
        {step === 'profile' && renderProfileStep()}
        {step === 'verify' && renderVerifyStep()}
        {step === 'complete' && renderCompleteStep()}
      </div>
    </div>
  );
};

export default OnboardingPage;